import React, { useContext } from 'react';
import styled from 'styled-components';
import {
  Button,
  Divider,
  Icon,
  Space,
  Text,
  Color,
  Alert,
  Flex,
  Spin,
} from '@bb/elements-web';
import { formatCurrency } from 'utils';
import { differenceInCalendarDays, lastDayOfMonth } from 'date-fns';
import { renderCreditCardIcon } from '../../components/ManageCard/CreditCardIcons';
import UpcomingChangesIcons from './icons/UpcomingChangesIcons';
import { useUpdatePaymentCardModalContext } from '../../components/ManagePaymentCard/UpdatePaymentCardModalContext';
import AnsweringServiceContext from '../AnsweringServicePage/AnsweringServiceContext';
import type { PaymentConfirmationBillingInfo } from './types/paymentConfirmationTypes';

type PaymentConfirmationContentProps = {
  billingInfo: PaymentConfirmationBillingInfo | undefined;
  handleAddAddressClick: () => void;
};

/**
 * Formats an expiry date string to "MMM YYYY" format (e.g., "Sep 2028")
 * Handles common formats: "MM/YY", "MM/YYYY", "MMYY", "MMYYYY", "YYYY-MM"
 */
export const formatExpiryDate = (expiryDate?: string): string => {
  if (!expiryDate) return '';

  let month: number;
  let year: number;

  // Handle ISO format (YYYY-MM)
  if (expiryDate.includes('-')) {
    const [yearStr, monthStr] = expiryDate.split('-');
    month = parseInt(monthStr, 10);
    year = parseInt(yearStr, 10);
  }
  // Handle slash format (MM/YY or MM/YYYY)
  else if (expiryDate.includes('/')) {
    const [monthStr, yearStr] = expiryDate.split('/');
    month = parseInt(monthStr, 10);
    year = parseInt(yearStr, 10);
    // Convert 2-digit year to 4-digit
    if (year < 100) {
      year += 2000;
    }
  }
  // Handle concatenated format (MMYY or MMYYYY)
  else {
    month = parseInt(expiryDate.substring(0, 2), 10);
    const yearStr = expiryDate.substring(2);
    year = parseInt(yearStr, 10);
    if (year < 100) {
      year += 2000;
    }
  }

  if (Number.isNaN(month) || Number.isNaN(year) || month < 1 || month > 12) {
    return expiryDate;
  }

  const date = new Date(year, month - 1);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
  }).format(date);
};

/**
 * The page that shows during the Answering Service subscription flow
 * confirming the monetary total with the user and allowing them to make
 * any billing changes they want.
 */
const PaymentConfirmationContent: React.FC<PaymentConfirmationContentProps> = ({
  billingInfo,
  handleAddAddressClick,
}) => {
  const { setModalVisible: setJPMCModalVisible } =
    useUpdatePaymentCardModalContext();

  // Only populate the following values from the context when AnsweringServiceContext
  // is rendered around this element. (i.e. serviceType === 'liveConcierge').
  // Otherwise populate them with blank valid data to avoid eslint errors.
  const {
    viewingLevel = 'org',
    billingDetails,
    fetchingBillingInfo,
  } = useContext(AnsweringServiceContext);

  // Show spinner while fetching the billing details - this shouldn't be necessary but added for additional safety
  if (fetchingBillingInfo || !billingDetails) {
    return (
      <Flex justify="center" align="center" style={{ width: '100%' }}>
        <Spin dataCy="answering-service-subscribe-loading" size="large" />
      </Flex>
    );
  }

  const formattedAddress = () => {
    if (!billingInfo) return '';

    const { address1, address2, city, state, zipCode, country } = billingInfo;

    return `${address1}${address2 ? `, ${address2}` : ''}, ${city}, ${state}, ${zipCode}, ${country}`;
  };

  // calculate day difference between today and next billing cycle start date to determine if we should show prorated fee info
  const today = new Date();
  const nextBillingCycleStartDate = new Date(
    `${billingDetails.nextBillingCycleStartDate.split('T')[0]}T00:00:00`, // remove the timestamp to avoid timezone issues
  );

  /** Determines whether today is the billing day based on the billing frequency and next billing cycle start date
   * - Monthly billing: if today is the same day of month as the billing cycle start date
   * - Yearly billing: if today is the same day and month as the billing cycle start date
   */
  const determineIsOnBillingDay = (): boolean => {
    if (!billingDetails?.billingFrequency) return false;

    const currentDay = today.getDate();
    const billingDay = nextBillingCycleStartDate.getDate();

    const currentMonth = today.getMonth();
    const billingMonth = nextBillingCycleStartDate.getMonth();

    const lastDayOfCurrentMonth = lastDayOfMonth(today).getDate();
    const lastDayOfBillingMonth = lastDayOfMonth(
      nextBillingCycleStartDate,
    ).getDate();

    const onBillingDay =
      currentDay === billingDay ||
      (currentDay === lastDayOfCurrentMonth &&
        billingDay === lastDayOfBillingMonth);

    const onBillingMonth = currentMonth === billingMonth;

    switch (billingDetails.billingFrequency) {
      case 'MONTHLY':
        return onBillingDay;
      case 'YEARLY':
        return onBillingDay && onBillingMonth;
      default:
        return false;
    }
  };

  const isOnBillingDay = determineIsOnBillingDay();

  /** The number of days between today and the next billing cycle start date - order here matters */
  const dayDifference = differenceInCalendarDays(
    nextBillingCycleStartDate,
    today,
  );

  /** The abbreviated billing frequency (e.g., "mo" for monthly) */
  const abbreviatedBillingFrequency = () => {
    if (!billingDetails?.billingFrequency) return '';

    switch (billingDetails.billingFrequency) {
      case 'MONTHLY':
        return 'mo';
      case 'YEARLY':
        return 'yr';
      case 'QUARTERLY':
        return 'qtr';
      default:
        return billingDetails.billingFrequency;
    }
  };

  /** The subscription starting date formatted as "MMM DD" (e.g., "Sep 15") */
  const subscriptionStartingDate = nextBillingCycleStartDate.toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
    },
  );

  const billingInfoHasContactInfo = !!(
    billingInfo &&
    billingInfo.address1 &&
    billingInfo.city &&
    billingInfo.state &&
    billingInfo.zipCode &&
    billingInfo.country &&
    billingInfo.email
  );

  const billingInfoHasPaymentInfo = !!(
    billingInfo &&
    billingInfo.cardholderName &&
    billingInfo.ccExpiryDate &&
    billingInfo.ccNumber &&
    billingInfo.ccType &&
    billingInfo.maskedCCNumber
  );

  return (
    <Space direction="vertical" size={40}>
      {
        <ServiceListingsContainer>
          <IconCircle>
            <Icon
              icon="liveConcierge"
              size={26}
              color="primary"
              style={{ display: 'flex' }}
            />
          </IconCircle>
          <TextContainer>
            <Text size="large" thickness="semiBold">
              Live Concierge
            </Text>
            {viewingLevel === 'org' && (
              <Text color="grey" size="large">
                {billingDetails.billableAgentCount} agents
              </Text>
            )}
          </TextContainer>
        </ServiceListingsContainer>
      }

      <StyledContainer $fontSize={16}>
        <SubTitle>Upcoming Changes</SubTitle>

        <StyledContainer $flexDirection="row" style={{ flexShrink: 1 }}>
          <UpcomingChangesIcons showTwoIcons={!isOnBillingDay} />
          <StyledUpcomingChangesContentContainer>
            {!isOnBillingDay && (
              <StyledUpcomingChangesContentItemContainer>
                <div style={{ flexGrow: 1 }}>
                  <Text thickness="bold" size="large">
                    Today
                  </Text>
                  <Text style={{ display: 'block' }} color="grey" size="large">
                    {dayDifference} days prorated fee
                  </Text>
                </div>
                <Text thickness="bold" size="large">
                  {formatCurrency(billingDetails.proratedFee, {
                    currency: billingDetails.currencyCode,
                  })}
                </Text>
              </StyledUpcomingChangesContentItemContainer>
            )}

            <StyledUpcomingChangesContentItemContainer>
              <div style={{ flexGrow: 1 }}>
                <Text
                  thickness="bold"
                  style={{ display: 'block' }}
                  size="large"
                >
                  Starting {isOnBillingDay ? 'Today' : subscriptionStartingDate}
                </Text>
              </div>
              <div style={{ flexGrow: 1, textAlign: 'right' }}>
                <Text thickness="bold" size="large">
                  {formatCurrency(billingDetails.totalRate, {
                    currency: billingDetails.currencyCode,
                  })}{' '}
                  / {abbreviatedBillingFrequency()}
                </Text>
                <Text
                  style={{ display: 'block', textAlign: 'right' }}
                  color="grey"
                >
                  + applicable taxes
                </Text>
              </div>
            </StyledUpcomingChangesContentItemContainer>
          </StyledUpcomingChangesContentContainer>
        </StyledContainer>

        {viewingLevel === 'org' && (
          <Alert
            type="info"
            message="Your month-to-month total may change as it is dependent upon the
                number of agents in your brokerage at the time of billing."
            style={{ fontSize: 14, padding: '16px 24px' }}
          />
        )}
      </StyledContainer>

      <StyledContainer>
        <Flex justify="space-between" align="center">
          <SubTitle style={{ display: 'block' }}>Contact & Address</SubTitle>
          {billingInfoHasContactInfo && (
            <ActionButton
              onClick={handleAddAddressClick}
              dataCy="edit-email-btn"
              minWidth="auto"
              style={{ padding: '4px 16px' }}
            >
              Edit
            </ActionButton>
          )}
        </Flex>
        {billingInfoHasContactInfo ? (
          <>
            <ContactInfoItemContainer>
              <Text style={{ display: 'block' }} color="grey" size="large">
                Email
              </Text>
              <Text size="large">{billingInfo?.email || `\u00A0`}</Text>
            </ContactInfoItemContainer>

            <ContactInfoItemContainer>
              <Text style={{ display: 'block' }} color="grey" size="large">
                Address
              </Text>
              <Text size="large">{formattedAddress() || `\u00A0`}</Text>
            </ContactInfoItemContainer>
          </>
        ) : (
          <ActionButton
            onClick={handleAddAddressClick}
            type="secondary"
            dataCy="add-btn"
            minWidth="auto"
            style={{
              paddingLeft: '16px',
              paddingRight: '16px',
              width: 'fit-content',
            }}
          >
            Add
          </ActionButton>
        )}
      </StyledContainer>

      <StyledContainer>
        <SubTitle style={{ display: 'block' }}>Payment Details</SubTitle>
        {billingInfoHasPaymentInfo ? (
          <StyledContainer $flexDirection="row" $alignItems="center" $gap={24}>
            {renderCreditCardIcon(billingInfo.ccType?.toLowerCase() || '', 36)}
            <StyledContainer style={{ flex: '1 0 0' }} $gap={8}>
              <SubTitle>
                {billingInfo.ccType} ending in{' '}
                {billingInfo.maskedCCNumber.slice(-4)}
              </SubTitle>
              <Text color="grey" size="large">
                Expires {formatExpiryDate(billingInfo?.ccExpiryDate)}
              </Text>
            </StyledContainer>
            <ActionButton
              onClick={() => setJPMCModalVisible(true)}
              dataCy="edit-btn"
              minWidth="auto"
              style={{ padding: '4px 16px' }}
            >
              Update
            </ActionButton>
          </StyledContainer>
        ) : (
          <StyledContainer $alignItems="flex-start" $gap={8}>
            <StyledPaymentButton
              size="middle"
              type="secondary"
              onClick={() => setJPMCModalVisible(true)}
              dataCy="add-cc-btn"
            >
              Add credit card
            </StyledPaymentButton>
            <Text color="grey" size="large" style={{ display: 'block' }}>
              Powered by JPMC
            </Text>
          </StyledContainer>
        )}
      </StyledContainer>

      <div>
        <Text color="grey" style={{ fontSize: 16, display: 'block' }}>
          {!isOnBillingDay &&
            'You will be paying a prorated fee today to activate the service immediately. '}
          Your next invoice will start on{' '}
          <span style={{ fontWeight: 'bold' }}>{subscriptionStartingDate}</span>{' '}
          and will renew automatically every month. Cancel anytime in
          Subscription Settings. By continuing, you agree to{' '}
          <StyledTOCLink
            href="https://www.brokerbay.com/terms-of-service"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </StyledTOCLink>
          .
        </Text>
        <Divider />
      </div>
    </Space>
  );
};

const StyledTOCLink = styled.a`
  font-family: inherit;
  font-weight: bold;

  && {
    text-decoration: underline;
    color: ${Color.SUVA_GREY};
  }
`;

const StyledContainer = styled.div<{
  $flexDirection?: 'row' | 'column';
  $justifyContent?: string;
  $alignItems?: string;
  $alignSelf?: string;
  $fontSize?: number;
  $gap?: number;
}>`
  display: flex;
  flex-direction: ${props => props.$flexDirection || 'column'};
  gap: ${props => props.$gap || 16}px;
  ${({ $fontSize }) => $fontSize && `font-size: ${$fontSize}px;`}
  ${({ $justifyContent }) =>
    $justifyContent && `justify-content: ${$justifyContent};`}
  ${({ $alignItems }) => $alignItems && `align-items: ${$alignItems};`}
  ${({ $alignSelf }) => $alignSelf && `align-self: ${$alignSelf};`}
`;

const StyledUpcomingChangesContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-self: stretch;
  flex-grow: 1;
`;

const StyledUpcomingChangesContentItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 7px;
  align-self: stretch;
`;

const ContactInfoItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  font-size: 16px;
  gap: 4px;
`;

const ActionButton = styled(Button)`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const SubTitle = styled.span`
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
`;

const StyledPaymentButton = styled(Button)`
  --bb-blue: ${Color.PRIMARY};

  display: flex;
  padding: 0 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  border: 1px solid var(--bb-blue);
  color: var(--bb-blue);
  font-size: 16px;
`;

const ServiceListingsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 1em;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  min-width: 36px;
  background-color: ${Color.VEGETA_10};
  border-radius: 50%;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default PaymentConfirmationContent;
